import React, {useState} from 'react';
import './LobbyFactionPicker.css';
import PageButton from '../../elements/PageButton/PageButton';
import LobbyFactionCarousel from './LobbyFactionCarousel';
import LobbyFactionGrid from './LobbyFactionGrid';
import {getLobbyFactionDisplay} from './lobbyFactionDisplay';
import {useFactionSelectList} from '../../../hooks/factions/useFactionSelectList';
import {useFactionSelectPurchase} from '../../../hooks/factions/useFactionSelectPurchase';
import FactionSelectPurchaseModals, {
  renderFactionLockedSelectButton,
  TEXT_SHOP_LEAVES_LOBBY,
} from '../../pages/FactionSelectPage/FactionSelectPurchase';
import {checkIfColorNeedsDarkText} from '../../../utils/other/checkIfColorNeedsDarkText';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {RANDOM_FACTION_ID} from '../../../configs/factions';

// Вид встроенного выбора фракции: карусель или все фракции сразу
export const LOBBY_FACTION_PICKER_VIEWS = {
  CAROUSEL: 'carousel',
  GRID: 'grid',
};

// Обычное лобби: выданных фракций нет, список — все фракции игры
const NO_RANKED_FACTION_IDS = [];

const getItemDisplay = (item) => getLobbyFactionDisplay(item.id, item.skinId);

// Компактный вид — только то, что можно выбрать сейчас или купить: фракции
// «в разработке» выбрать нельзя никак, а 12 лишних плиток сделали бы все
// плитки сетки вдвое мельче. Они остаются в карусели (там же «Получить»)
const checkIfGridItem = (item) => item.id === RANDOM_FACTION_ID || item.implemented;

// Капс дает оформление маски
const getItemMaskLabel = (item) => (item.implemented ? 'Не открыта' : 'В разработке');

/**
 * Встроенный выбор фракции своей карточки обычного сетевого лобби на
 * десктопе. Состав, порядок и доступность — те же, что на экране выбора
 * фракции (useFactionSelectList), покупка закрытых — те же кнопки и модалки
 * (useFactionSelectPurchase). Выбор — сразу, без полноэкранного ритуала:
 * звук, SELECT_FACTION и короткий эффект на арте (ownFactions.onSelect).
 *
 * @param {Object} props
 * @param {Object} props.auth - private.auth
 * @param {Object} props.actingPlayer
 * @param {string} props.viewMode - LOBBY_FACTION_PICKER_VIEWS
 * @param {Function} props.onViewModeChange
 * @param {Object} props.ownFactions - обработчики выбора из LobbyPage
 */
const LobbyFactionPicker = ({auth, actingPlayer, viewMode, onViewModeChange, ownFactions}) => {
  const {factionItems, checkIfFactionAvailable, checkIfDetailsAvailable} = useFactionSelectList({
    auth, availableFactionIds: NO_RANKED_FACTION_IDS, isRanked: false,
  });
  const purchase = useFactionSelectPurchase(auth);

  const selectedFactionId = actingPlayer?.factionId || null;
  // Карусель открывается на текущей фракции игрока, иначе на первом элементе
  // («Случайная фракция»)
  const [currentIndex, setCurrentIndex] = useState(
    () => Math.max(0, factionItems.findIndex((item) => item.id === selectedFactionId))
  );
  const currentItem = factionItems[Math.min(currentIndex, factionItems.length - 1)];

  const gridItems = factionItems.filter(checkIfGridItem);

  // Закрытая фракция в сетке — переход к ней в карусели: покупка там. Индекс
  // — в полном списке карусели, а не в урезанной сетке
  const handleGridItemClick = (item) => {
    if (checkIfFactionAvailable(item)) {
      ownFactions.onSelect(item.id);
      return;
    }
    MENU_SOUND_MANAGER.playNextCarouselItemClick();
    setCurrentIndex(Math.max(0, factionItems.findIndex((carouselItem) => carouselItem.id === item.id)));
    onViewModeChange(LOBBY_FACTION_PICKER_VIEWS.CAROUSEL);
  };

  const renderCarouselControls = () => {
    if (!currentItem) return null;
    const display = getItemDisplay(currentItem);
    const isAvailable = checkIfFactionAvailable(currentItem);
    const isCurrentSelected = currentItem.id === selectedFactionId;
    // Кнопка выбора — цветом фракции; на ярком цвете светлый текст не
    // читается, там он темный (как в карусели выбора фракции)
    const selectButtonColor = isCurrentSelected ? undefined : display.textColor;
    const isDarkText = checkIfColorNeedsDarkText(selectButtonColor);
    const lockedButton = renderFactionLockedSelectButton({
      item: currentItem,
      isAvailable,
      isBanned: false,
      purchase,
      pageId: 'LobbyPage',
      className: 'lobby-faction-picker_select',
    });
    return (
      <div className="lobby-faction-picker_controls">
        <div className="lobby-faction-picker_caption">
          <div className="lobby-faction-picker_name" style={{color: display.textColor}}>{display.name}</div>
          {display.skinName && <div className="lobby-faction-picker_skin">{display.skinName}</div>}
        </div>
        <div className="lobby-faction-picker_buttons">
          {lockedButton || (
            <PageButton
              className={`lobby-faction-picker_select ${isDarkText ? 'lobby-faction-picker_select_dark-text' : ''} ${
                isCurrentSelected ? 'lobby-faction-picker_select_current' : ''}`}
              // Звук и аналитика выбора — в самом обработчике
              noSound
              onClick={() => ownFactions.onSelect(currentItem.id)}
              disabled={isCurrentSelected || ownFactions.isSelectBlocked}
              style={{backgroundColor: selectButtonColor}}
            >
              {isCurrentSelected ? 'Выбрана' : 'Выбрать'}
            </PageButton>
          )}
          <PageButton
            className="lobby-faction-picker_details"
            noSound
            onClick={() => ownFactions.onOpenOwnCard(currentItem.id, currentItem.skinId, isAvailable)}
            disabled={!checkIfDetailsAvailable(currentItem)}
            pageId="LobbyPage"
            buttonId="show_faction_details"
            buttonValue={currentItem.id}
          >
            Подробно
          </PageButton>
        </div>
      </div>
    );
  };

  return (
    <div className="lobby-faction-picker">
      {viewMode === LOBBY_FACTION_PICKER_VIEWS.GRID ? (
        <LobbyFactionGrid
          items={gridItems}
          selectedFactionId={selectedFactionId}
          getItemDisplay={getItemDisplay}
          checkIfItemAvailable={checkIfFactionAvailable}
          getItemMaskLabel={getItemMaskLabel}
          onItemClick={handleGridItemClick}
          selectFlash={ownFactions.selectFlash}
        />
      ) : (
        <LobbyFactionCarousel
          items={factionItems}
          currentIndex={currentIndex}
          onIndexChange={setCurrentIndex}
          getItemDisplay={getItemDisplay}
          checkIfItemAvailable={checkIfFactionAvailable}
          getItemMaskLabel={getItemMaskLabel}
          selectFlash={ownFactions.selectFlash}
          selectedFactionId={selectedFactionId}
        >
          {renderCarouselControls()}
        </LobbyFactionCarousel>
      )}

      <FactionSelectPurchaseModals
        purchase={purchase}
        insufficientCrystalsWarningText={TEXT_SHOP_LEAVES_LOBBY}
        onGoToCrystals={ownFactions.onGoToCrystals}
        onOpenInventory={ownFactions.onOpenInventory}
      />
    </div>
  );
};

export default LobbyFactionPicker;
